<template>
  <div class="flex max-h-[80vh] flex-col">
    <div class="border-b border-border-default px-6 py-4">
      <span class="text-base font-semibold text-text-primary">
        {{ $t('area.dhl.shipments.title') }}
      </span>
    </div>

    <div class="flex-1 overflow-y-auto">
      <div
        v-if="sortedShipments.length === 0"
        class="px-6 py-10 text-center text-sm text-text-secondary">
        {{ $t('area.dhl.shipments.message.none-yet') }}
      </div>

      <div
        v-else
        class="divide-y divide-border-default">
        <div
          v-for="shipment in sortedShipments"
          :key="shipment.id"
          class="px-6 py-4">
          <div
            class="flex cursor-pointer items-center gap-4"
            @click="toggleExpanded(shipment)">
            <div class="flex min-w-0 flex-1 items-center gap-4">
              <span class="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
                {{ orderNumberLabel(shipment) }}
              </span>
              <span class="shrink-0 text-sm text-text-secondary">
                {{ shipment.targetPickupDate }}
              </span>
              <span
                class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium"
                :class="shipmentStatusClass(shipment.status)">
                {{ shipmentStatusLabel(shipment.status) }}
              </span>
              <span
                v-if="shipment.timestamps.lastAttemptAt"
                class="text-xs text-text-secondary">
                {{ $t('area.dhl.shipment.field.last-attempted') }}
                {{ $d(shipment.timestamps.lastAttemptAt, 'shortMonthDateTime') }}
              </span>
            </div>
            <IconLucideChevronDown
              class="h-4 w-4 shrink-0 text-text-secondary transition-transform"
              :class="isExpanded(shipment.id) ? 'rotate-180' : ''" />
          </div>

          <div
            v-if="isExpanded(shipment.id)"
            class="mt-4 flex flex-col gap-4">
            <div>
              <span class="mb-2 block text-xs font-medium uppercase tracking-wide text-text-secondary">
                {{ $t('area.dhl.shipment.field.step') }}
              </span>
              <div class="flex flex-col gap-2">
                <div
                  v-for="stepKey in stepOrder"
                  :key="stepKey"
                  class="flex items-start gap-3 rounded-lg bg-gray-50 px-3 py-2">
                  <span
                    class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium"
                    :class="stepStatusClass(shipment.steps[stepKey].status)">
                    {{ stepStatusLabel(shipment.steps[stepKey].status) }}
                  </span>
                  <div class="flex min-w-0 flex-1 flex-col gap-1">
                    <div class="flex items-center gap-2">
                      <span class="text-sm text-text-primary">
                        {{ stepLabel(stepKey) }}
                      </span>
                      <span
                        v-if="shipment.steps[stepKey].timestamps.completedAt"
                        class="text-xs text-text-secondary">
                        {{ $d(shipment.steps[stepKey].timestamps.completedAt, 'shortMonthDateTime') }}
                      </span>
                    </div>
                    <div
                      v-if="shipment.steps[stepKey].error"
                      class="break-all rounded bg-red-50 px-2 py-1 font-mono text-xs text-red-700">
                      {{ shipment.steps[stepKey].error }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="shipment.result">
              <span class="mb-2 block text-xs font-medium uppercase tracking-wide text-text-secondary">
                {{ $t('area.dhl.shipment.field.result') }}
              </span>
              <div class="flex flex-col gap-1 rounded-lg bg-gray-50 px-3 py-2 text-sm">
                <div class="flex gap-2">
                  <span class="w-36 shrink-0 text-text-secondary">
                    {{ $t('area.dhl.shipment.field.shipment-number') }}
                  </span>
                  <span class="break-all font-mono text-text-primary">
                    {{ shipment.result.shipmentID }}
                  </span>
                </div>
                <div class="flex gap-2">
                  <span class="w-36 shrink-0 text-text-secondary">
                    {{ $t('area.dhl.shipment.field.tracking-code') }}
                  </span>
                  <span class="font-mono text-text-primary">
                    {{ shipment.result.trackingCode }}
                  </span>
                </div>
                <div
                  v-if="shipment.result.dispatchConfirmationNumber"
                  class="flex gap-2">
                  <span class="w-36 shrink-0 text-text-secondary">
                    {{ $t('area.dhl.shipment.field.pickup-confirmation') }}
                  </span>
                  <span class="font-mono text-text-primary">
                    {{ shipment.result.dispatchConfirmationNumber }}
                  </span>
                </div>
                <div class="flex gap-2">
                  <span class="w-36 shrink-0 text-text-secondary">
                    {{ $t('area.dhl.shipment.field.product-code') }}
                  </span>
                  <span class="font-mono text-text-primary">
                    {{ shipment.result.productCode }}
                  </span>
                </div>
                <div
                  v-if="shipment.result.estimatedPickupTime"
                  class="flex flex-col gap-0.5">
                  <div class="flex gap-2">
                    <span class="w-36 shrink-0 text-text-secondary">
                      {{
                        isServicePointShipment(shipment)
                          ? $t('area.dhl.shipment.message.expected-pickup-today')
                          : $t('area.dhl.shipment.field.pickup-deadline')
                      }}
                    </span>
                    <span class="text-text-primary">
                      {{ formatEstimatedPickupTime(shipment.result.estimatedPickupTime) }}
                    </span>
                  </div>
                  <span
                    v-if="isServicePointShipment(shipment)"
                    class="text-xs text-text-secondary">
                    {{ $t('area.dhl.shipment.message.time-approximate') }}
                  </span>
                </div>
              </div>
              <div v-if="shipment.result.labelFreeCodePath">
                <img
                  v-if="qrCodeUrls[shipment.id]"
                  :src="qrCodeUrls[shipment.id]"
                  alt="DHL QR-kode"
                  class="h-24 w-24" />
                <span
                  v-else-if="loadingQrCodeShipmentIDs.includes(shipment.id)"
                  class="text-xs text-text-secondary">
                  {{ $t('area.dhl.shipment.message.loading-qr') }}
                </span>
              </div>
              <div v-if="shipment.result.labelDocumentPath">
                <UiBaseButton
                  size="sm"
                  variant="secondary"
                  :isLoading="loadingLabelShipmentIDs.includes(shipment.id)"
                  :text="$t('area.dhl.shipment.action.download-label')"
                  @button-click="onDownloadLabelClick(shipment)" />
              </div>
            </div>

            <span
              v-if="isStuck(shipment)"
              class="mb-2 block text-xs text-text-secondary">
              {{ $t('area.dhl.shipment.message.taking-longer') }}
            </span>

            <div
              v-if="shipment.status === 'failed' || isStuck(shipment)"
              class="flex gap-2">
              <UiBaseButton
                size="sm"
                variant="secondary"
                :isLoading="retryingShipmentIDs.includes(shipment.id)"
                :text="$t('area.dhl.shipment.action.retry')"
                @button-click="onRetryClick(shipment)" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useOrderStore } from '@/stores/orderStore'
import { useDhlShipmentStore } from '@/stores/dhlShipmentStore'
import { useAppStore } from '@/stores/appStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { mapState, mapActions } from 'pinia'
import { storage } from '@/firebase/init'
import { getDownloadURL, ref as storageRef } from 'firebase/storage'
import type {
  IDhlShipment,
  TDhlShipmentStatus,
  TDhlShipmentStepStatus,
} from '@team-milva/lab-models/resources'
import { resolveIsInternal } from '@team-milva/lab-models'
import { formatOrderNumber } from '@/globals/typescript/utils/internalLabel'

/** A real DHL rates/shipment call completes in seconds, even accounting for a Cloud Functions
 *  cold start — 1 minute leaves a comfortable safety margin while still surfacing a retry
 *  option quickly, rather than waiting for the scheduled backend backstop
 *  (`checkStuckDhlShipments`, 30m) to fire. */
const STUCK_THRESHOLD_MS = 60 * 1000

export default {
  name: 'DhlShipmentsModal',

  data() {
    return {
      expandedShipmentIDs: [] as string[],
      retryingShipmentIDs: [] as string[],
      loadingLabelShipmentIDs: [] as string[],
      qrCodeUrls: {} as Record<string, string>,
      loadingQrCodeShipmentIDs: [] as string[],
      // Execution order (see runDhlShipment.ts) — not necessarily the Firestore map's key order.
      stepOrder: ['ratesCheck', 'shipmentCreation', 'pickupCancellation'] as const,
      mountedAt: Date.now(),
      nowTick: Date.now(),
      stuckCheckInterval: null as ReturnType<typeof setInterval> | null,
    }
  },

  computed: {
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useDhlShipmentStore, ['allDhlShipments']),
    ...mapState(useAppStore, ['currentLocale']),

    sortedShipments(): IDhlShipment[] {
      return [...this.allDhlShipments].sort(
        (a, b) => (b.timestamps.lastAttemptAt ?? 0) - (a.timestamps.lastAttemptAt ?? 0)
      )
    },

    dhlDateLocale(): string {
      return this.currentLocale === 'da' ? 'da-DK' : 'en-GB'
    },
  },

  methods: {
    ...mapActions(useOrderStore, ['retryDhlShipment']),

    orderNumberLabel(shipment: IDhlShipment): string {
      const order = this.allOrders.find((x) => x.id === shipment.orderID)
      if (!order?.orderNumber) return shipment.id
      return formatOrderNumber(order.orderNumber, resolveIsInternal(order.options))
    },

    toggleExpanded(shipment: IDhlShipment): void {
      const index = this.expandedShipmentIDs.indexOf(shipment.id)
      if (index >= 0) {
        this.expandedShipmentIDs.splice(index, 1)
      } else {
        this.expandedShipmentIDs.push(shipment.id)
        this.loadQrCodeUrl(shipment)
      }
    },

    async loadQrCodeUrl(shipment: IDhlShipment): Promise<void> {
      const path = shipment.result?.labelFreeCodePath
      if (!path || this.qrCodeUrls[shipment.id]) return
      this.loadingQrCodeShipmentIDs.push(shipment.id)
      try {
        const fileRef = storageRef(storage, path)
        this.qrCodeUrls[shipment.id] = await getDownloadURL(fileRef)
      } catch {
        useToast().error(this.$t('error.common.standard'))
      } finally {
        const index = this.loadingQrCodeShipmentIDs.indexOf(shipment.id)
        if (index >= 0) {
          this.loadingQrCodeShipmentIDs.splice(index, 1)
        }
      }
    },

    isExpanded(shipmentID: string): boolean {
      return this.expandedShipmentIDs.includes(shipmentID)
    },

    // `lastAttemptAt` is only set once `runDhlShipment` actually starts — a shipment still
    // 'pending' (never attempted) has no such timestamp, so this modal's own mount time is
    // the best available reference for "how long has this looked stuck".
    isStuck(shipment: IDhlShipment): boolean {
      if (shipment.status !== 'pending' && shipment.status !== 'in-progress') return false
      const since = shipment.timestamps.lastAttemptAt ?? this.mountedAt
      return this.nowTick - since > STUCK_THRESHOLD_MS
    },

    shipmentStatusClass(status: TDhlShipmentStatus): string {
      const classes: Record<TDhlShipmentStatus, string> = {
        pending: 'bg-gray-100 text-gray-600',
        'in-progress': 'bg-yellow-100 text-yellow-700',
        completed: 'bg-green-100 text-green-700',
        failed: 'bg-red-100 text-red-700',
        cancelled: 'bg-gray-100 text-gray-600',
      }
      return classes[status] ?? 'bg-gray-100 text-gray-600'
    },

    shipmentStatusLabel(status: TDhlShipmentStatus): string {
      const labels: Record<TDhlShipmentStatus, string> = {
        pending: this.$t('area.dhl.status.pending'),
        'in-progress': this.$t('area.dhl.status.in-progress'),
        completed: this.$t('area.dhl.status.completed'),
        failed: this.$t('area.dhl.status.failed'),
        cancelled: this.$t('area.dhl.status.cancelled'),
      }
      return labels[status] ?? status
    },

    stepStatusClass(status: TDhlShipmentStepStatus): string {
      const classes: Record<TDhlShipmentStepStatus, string> = {
        pending: 'bg-gray-100 text-gray-600',
        completed: 'bg-green-100 text-green-700',
        failed: 'bg-red-100 text-red-700',
      }
      return classes[status]
    },

    stepStatusLabel(status: TDhlShipmentStepStatus): string {
      const labels: Record<TDhlShipmentStepStatus, string> = {
        pending: this.$t('area.dhl.status.pending'),
        completed: this.$t('area.dhl.status.completed'),
        failed: this.$t('area.dhl.status.failed'),
      }
      return labels[status]
    },

    stepLabel(stepKey: string): string {
      const labels: Record<string, string> = {
        ratesCheck: this.$t('area.dhl.shipment.field.rates-check'),
        shipmentCreation: this.$t('area.dhl.shipment.field.shipment-creation'),
        pickupCancellation: this.$t('area.dhl.shipment.action.cancel-pickup-title'),
      }
      return labels[stepKey] ?? stepKey
    },

    // Service points never request a DHL pickup (see DHL Integration feature
    // doc) — the cutoff DHL returns for them is just today's approximate
    // collection window, not a deadline the lab/customer needs to meet.
    isServicePointShipment(shipment: IDhlShipment): boolean {
      return shipment.type === 'dhl-service-point'
    },

    formatEstimatedPickupTime(raw: string): string {
      return new Date(raw).toLocaleString(this.dhlDateLocale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    async onRetryClick(shipment: IDhlShipment): Promise<void> {
      const orderID = shipment.orderID
      if (!orderID) return
      this.retryingShipmentIDs.push(shipment.id)
      try {
        await this.retryDhlShipment(orderID)
        useToast().success(this.$t('area.dhl.shipment.message.restarted'))
      } catch {
        useToast().error(this.$t('error.common.standard'))
      } finally {
        const index = this.retryingShipmentIDs.indexOf(shipment.id)
        if (index >= 0) {
          this.retryingShipmentIDs.splice(index, 1)
        }
      }
    },

    async onDownloadLabelClick(shipment: IDhlShipment): Promise<void> {
      const path = shipment.result?.labelDocumentPath
      if (!path) return
      this.loadingLabelShipmentIDs.push(shipment.id)
      try {
        const fileRef = storageRef(storage, path)
        const url = await getDownloadURL(fileRef)
        window.open(url, '_blank')
      } catch {
        useToast().error(this.$t('error.common.standard'))
      } finally {
        const index = this.loadingLabelShipmentIDs.indexOf(shipment.id)
        if (index >= 0) {
          this.loadingLabelShipmentIDs.splice(index, 1)
        }
      }
    },

  },

  mounted(): void {
    this.stuckCheckInterval = setInterval(() => {
      this.nowTick = Date.now()
    }, 10_000)
  },

  beforeUnmount(): void {
    if (this.stuckCheckInterval) clearInterval(this.stuckCheckInterval)
  },
}
</script>
